<template>
  <div class="p-contentplans-writing-heading-count">
    <div class="p-contentplans-writing-heading-count__header">
      <h4 class="p-contentplans-writing__title--sub">
        見出し(h1～h3)の個数
      </h4>
    </div>
    <Advice v-if="isError" img="error" priority="low-gray">
      <template slot="title">
        <p>データが取得できませんでした。</p>
      </template>
      <p>
        時間を置いても取得できない場合は、チャットにてお問い合わせください。
      </p>
    </Advice>
    <Advice
      v-else-if="!isSerpsLoading && !serpsTopItems.length"
      img="error"
      priority="low-gray"
    >
      <template slot="title">
        調査したいキーワードではGoogleの検索結果が0件でした。
      </template>
      <p>
        実際の検索結果が0件ではない場合は、チャットにてお問い合わせください。
      </p>
    </Advice>
    <LoadingChara v-else-if="isLoading" variant="inline" size="sm">
      <template slot="title">データ取得中</template>
    </LoadingChara>
    <template v-else>
      <div class="typical-value" v-if="average">
        <div class="typical-value__item">
          <span class="min">h1～h3の総数の目安（平均）</span
          ><span class="num-txt"
            ><span class="num">{{
              average | roundFloatingPointNumber(1) | separateNumber
            }}</span
            ><span class="txt">個</span></span
          >
        </div>
        <div class="typical-value__item">
          <span class="min">h1の目安（中央値）</span
          ><span class="num-txt"
            ><span class="num">{{
              h1Median | roundFloatingPointNumber(1) | separateNumber
            }}</span
            ><span class="txt">個</span></span
          >
        </div>
        <div class="typical-value__item">
          <span class="min">h2の目安（中央値）</span
          ><span class="num-txt"
            ><span class="num">{{
              h2Median | roundFloatingPointNumber(1) | separateNumber
            }}</span
            ><span class="txt">個</span></span
          >
        </div>
        <div class="typical-value__item">
          <span class="min">h3の目安（中央値）</span
          ><span class="num-txt"
            ><span class="num">{{
              h3Median | roundFloatingPointNumber(1) | separateNumber
            }}</span
            ><span class="txt">個</span></span
          >
        </div>
      </div>
      <ul class="c-graph-headingCount">
        <li class="c-graph-headingCount__item c-graph-headingCount__item--rank">
          h1
        </li>
        <li class="c-graph-headingCount__item c-graph-headingCount__item--rank">
          h2
        </li>
        <li class="c-graph-headingCount__item c-graph-headingCount__item--rank">
          h3
        </li>
      </ul>
      <ul class="graph-area graph-area__headingCount">
        <ListItemHeadingCount
          v-for="(item, index) in data.items"
          :key="index"
          :item="item"
          :max="max"
          :hasOwnSite="hasOwnSite"
        />
      </ul>
    </template>
  </div>
</template>

<script>
import Advice from '@/components/Advice';
import ListItemHeadingCount from '@/components/contentPlans/ListItemHeadingCount';
import LoadingChara from '@/components/LoadingChara';
import apiProjects from '@/api/projects';
import axios from 'axios';
import utils from '@/utils';

export default {
  props: [
    'createdAt',
    'keyword',
    'project',
    'serpsTopItems',
    'serpsAllItems',
    'isSerpsError',
    'isSerpsLoading'
  ],

  components: {
    Advice,
    ListItemHeadingCount,
    LoadingChara
  },

  data() {
    return {
      expiresAt: Date.now() + 10 * 60 * 1000,
      data: {
        items: []
      },
      isLoading: true,
      timeout: null,
      return: [],
      flg: true,
      isTdhError: false,
      hasOwnSite: true
    };
  },

  computed: {
    average() {
      let average = null;
      let count = this.data.items.length;
      let sum = 0;
      let sumCount = 0;
      for (let i = 0; i < count; i++) {
        // データ取得中（headers.totalがnull）、データ欠損（headers.totalがundefined）、PDFの場合は平均の計算に含めない
        if (
          this.data.items[i].headers.total === null ||
          typeof this.data.items[i].headers.total === 'undefined' ||
          this.data.items[i].contentType === 'pdf' ||
          !this.data.items[i].rank ||
          this.data.items[i].rank < 1 ||
          this.data.items[i].rank > 10
        ) {
          continue;
        }
        sum += this.data.items[i].headers.total;
        sumCount++;
      }
      if (sum && sumCount) {
        average = sum / sumCount;
      }
      return average;
    },
    max() {
      let max = 0;
      let count = this.data.items.length;
      for (let i = 0; i < count; i++) {
        let total = this.data.items[i].headers.total;
        if (total > max) {
          max = total;
        }
      }
      return max;
    },
    h1Median() {
      const filteredItems = this.data.items.filter(item => {
        return (
          item.contentType !== 'pdf' &&
          item.rank &&
          item.rank >= 1 &&
          item.rank <= 10 &&
          item.headers.h1 &&
          item.headers.h1.length > 0
        );
      });
      if (filteredItems.length === 0) {
        return 0;
      }
      const h1Items = filteredItems.map(item => {
        return item.headers.h1.length;
      });
      return utils.calcMedian(h1Items);
    },
    h2Median() {
      const filteredItems = this.data.items.filter(item => {
        return (
          item.contentType !== 'pdf' &&
          item.rank &&
          item.rank >= 1 &&
          item.rank <= 10 &&
          item.headers.h2 &&
          item.headers.h2.length > 0
        );
      });
      if (filteredItems.length === 0) {
        return 0;
      }
      const h2Items = filteredItems.map(item => {
        return item.headers.h2.length;
      });
      return utils.calcMedian(h2Items);
    },
    h3Median() {
      const filteredItems = this.data.items.filter(item => {
        return (
          item.contentType !== 'pdf' &&
          item.rank &&
          item.rank >= 1 &&
          item.rank <= 10 &&
          item.headers.h3 &&
          item.headers.h3.length > 0
        );
      });
      if (filteredItems.length === 0) {
        return 0;
      }
      const h3Items = filteredItems.map(item => {
        return item.headers.h3.length;
      });
      return utils.calcMedian(h3Items);
    },
    isError() {
      return this.isTdhError || this.isSerpsError;
    }
  },

  watch: {
    serpsTopItems: {
      handler() {
        this.init();
      },
      deep: true,
      immediate: true
    }
  },

  destroyed() {
    clearTimeout(this.timeout);

    // 非同期通信をキャンセル
    this.flg = false;
    for (let i = 0; i < this.return.length; i++) {
      if (this.return[i]) {
        this.return[i].cancel();
      }
    }
  },
  methods: {
    init() {
      if (!this.serpsTopItems.length) {
        return;
      }
      // 配列のコピー
      this.data.items = JSON.parse(JSON.stringify(this.serpsTopItems));

      for (let i = 0; i < this.data.items.length; i++) {
        if (this.data.items[i].url.indexOf(this.project.siteUrl) !== -1) {
          this.data.items[i].isOwnSite = true;
        } else {
          this.data.items[i].isOwnSite = false;
        }
      }

      // 10位までに登録サイトがない場合は追加
      if (!this.data.items.some(item => item.isOwnSite)) {
        this.data.items.push({
          url: null,
          rank: null,
          isOwnSite: true
        });
      }

      this.data.items = this.data.items.map((item, index) => {
        // 11位以下の自社サイトURLとrankの取得
        if (!item.rank) {
          for (let i = 0; i < this.serpsAllItems.length; i++) {
            if (
              this.serpsAllItems[i].url.indexOf(this.project.siteUrl) !== -1
            ) {
              item.url = this.serpsAllItems[i].url;
              item.rank = this.serpsAllItems[i].rank;
              break;
            }
          }
        }
        item.headers = {
          h1: null,
          h2: null,
          h3: null,
          h4: null,
          h5: null,
          total: null
        };
        return item;
      });

      this.data.items.map((item, index) => {
        return this.getContentPlansTitle(
          item.rank,
          item.url,
          index,
          item.isOwnSite
        );
      });
    },
    getContentPlansTitle(rank, url, index, isOwnSite) {
      // 圏外の場合はユーザーに見れないことを伝える
      if (!rank) {
        return;
      }
      // pdfの場合はリクエストせずにユーザーに取得できないことを伝える
      if (url && utils.isPdf(url)) {
        let total = 0;
        this.data.items.splice(index, 1, {
          headers: {
            h1: [],
            h2: [],
            h3: [],
            h4: [],
            h5: [],
            total: total
          },
          url: url,
          rank: rank,
          contentType: 'pdf',
          isOwnSite: isOwnSite
        });
        return;
      }
      let params = {
        dateJst: this.$options.filters.dateText(this.createdAt, 'YYYY-MM-DD'),
        device: 'desktop',
        url: url,
        resource: 'headers'
      };

      this.return[index] = axios.CancelToken.source();
      return apiProjects.contentPlans.tdh
        .get(
          params,
          this.auth.idToken,
          this.project.id,
          this.return[index].token,
          index + 1
        )
        .then(response => {
          let total = this.countTotal(response.data.headers);
          this.data.items.splice(index, 1, {
            headers: {
              h1: response.data.headers.h1,
              h2: response.data.headers.h2,
              h3: response.data.headers.h3,
              h4: response.data.headers.h4,
              h5: response.data.headers.h5,
              total: total
            },
            url: url,
            rank: rank,
            contentType: 'html',
            isOwnSite: isOwnSite
          });
          // 圏外とpdf以外の場合はtotalがあること
          const isComplete = this.data.items.every(item => {
            return (
              item.rank === null ||
              item.contentType === 'pdf' ||
              item.headers.total !== null
            );
          });
          if (isComplete) {
            this.$emit('completeHeaders');
          }
          this.scheduleGetTitle(rank, url, index, isOwnSite);
          if (!this.isTdhError) {
            this.isLoading = false;
            this.$nextTick(() => {
              setTimeout(() => {
                this.$emit('loadedHeaders');
              }, 20);
            });
          }
        })
        .catch(error => {
          if (!axios.isCancel(error)) {
            this.scheduleGetTitle(rank, url, index, isOwnSite);
          }
        })
        .finally(() => {});
    },
    countTotal(response) {
      let total = null;
      // h1、h2、h3はnullの時があるため
      if (response.h1) {
        total += response.h1.length;
      }
      if (response.h2) {
        total += response.h2.length;
      }
      if (response.h3) {
        total += response.h3.length;
      }
      return total;
    },
    scheduleGetTitle(rank, url, index, isOwnSite) {
      if (this.flg) {
        if (Date.now() < this.expiresAt) {
          this.timeout = setTimeout(() => {
            this.getContentPlansTitle(rank, url, index, isOwnSite);
          }, 30000);
        } else {
          this.isLoading = false;
          // グラフのバーの描画に20msのdelayがあるので少しだけ遅らせる
          this.$nextTick(() => {
            setTimeout(() => {
              this.$emit('loadedHeaders');
            }, 20);
          });
          if (this.data.items) {
            for (let i = 0; i < this.data.items.length; i++) {
              if (this.data.items[index].headers.total === null) {
                this.data.items.splice(index, 1, {
                  ...this.data.items[index],
                  headers: {
                    h1: null,
                    h2: null,
                    h3: null,
                    h4: null,
                    total: undefined
                  }
                });
              }
              if (
                this.data.items[i].headers.h1 === null ||
                this.data.items[i].headers.h1.length === 0
              ) {
                this.isTdhError = true;
              } else {
                this.isTdhError = false;
                break;
              }
            }
          } else {
            this.isTdhError = true;
          }
        }
      }
    }
  }
};
</script>
